// Server-jar catalog: which loaders Apect offers and which Minecraft versions each one has.
//
// Extracted from the per-server versions API route so the server-creation page can offer
// exactly the same jar types and versions as the in-panel version switcher. Both import
// from here; neither keeps its own copy.
//
// Build listings and download resolution stay in the versions route, since they are only
// needed once a server exists.

import {
  FABRIC_VERSIONS,
  FABRIC_DEFAULT_VERSION,
  NEOFORGE_VERSIONS,
  NEOFORGE_DEFAULT_VERSION,
  neoforgeVersionInfo,
} from './jarVersions';

const JAR_TYPES = {
  paper: {
    id: 'paper',
    name: 'Paper',
    description: 'Fast, stable, and plugin-friendly Paper builds.',
    hasBuilds: true,
    family: 'bukkit',
  },
  purpur: {
    id: 'purpur',
    name: 'Purpur',
    description: 'Paper fork with extra gameplay and admin options.',
    hasBuilds: true,
    family: 'bukkit',
  },
  folia: {
    id: 'folia',
    name: 'Folia',
    description: 'PaperMC experimental region-threaded performance builds.',
    hasBuilds: true,
    family: 'bukkit',
  },
  leaf: {
    id: 'leaf',
    name: 'Leaf',
    description: 'High-performance Paper fork (LeafMC) with the latest experimental builds.',
    hasBuilds: true,
    family: 'bukkit',
  },
  pufferfish: {
    id: 'pufferfish',
    name: 'Pufferfish',
    description: 'Performance-focused Paper fork built for large servers.',
    hasBuilds: true,
    family: 'bukkit',
  },
  spigot: {
    id: 'spigot',
    name: 'Spigot',
    description: 'The classic Bukkit server from SpigotMC.',
    hasBuilds: true,
    family: 'bukkit',
  },
  vanilla: {
    id: 'vanilla',
    name: 'Vanilla',
    description: 'Official Mojang server jars (releases and snapshots).',
    hasBuilds: false,
    family: null, // no plugin/mod loader - never touches ApectGuard or the template field
  },
  fabric: {
    id: 'fabric',
    name: 'Fabric',
    description: 'Lightweight modding platform. ApectGuard, Fabric API and FabricProxy-Lite are installed to match the version you pick.',
    hasBuilds: true,
    family: 'fabric',
  },
  neoforge: {
    id: 'neoforge',
    name: 'NeoForge',
    description: 'The Forge-family mod loader. ApectNeoProxy is installed to match the version you pick, which also handles Velocity forwarding.',
    hasBuilds: true,
    family: 'neoforge',
  },
};

const SUPPORTED_JAR_TYPES = Object.keys(JAR_TYPES);

// Spigot and Vanilla are only offered to standalone servers. Anything connected to the
// Apect proxy (every free-tier server, plus non-standalone paid servers) needs Velocity
// modern forwarding and ApectGuard: Vanilla has no plugin loader, and Spigot only speaks
// BungeeCord forwarding, so players coming through the proxy could never join either.
const PROXIED_JAR_TYPES = SUPPORTED_JAR_TYPES.filter((id) => !['spigot', 'vanilla'].includes(id));

function allowedJarTypesForServer(server) {
  return server?.standalone ? SUPPORTED_JAR_TYPES : PROXIED_JAR_TYPES;
}

function normalizeJarType(value) {
  const normalized = String(value || 'paper').toLowerCase();
  return SUPPORTED_JAR_TYPES.includes(normalized) ? normalized : null;
}

// Same check used across the frontend (ServerContent.tsx, PluginInstaller.tsx,
// PanelLayout.tsx, NavMenu.tsx) to decide "Mods" vs "Plugins" - mirrored here so the jar
// installer agrees with the rest of the app about which family a server is currently in.
//
// NeoForge has to be its own family rather than falling through to bukkit: the versions
// route compares this against the new jar's family to decide whether to rewrite the
// server's template, and lumping it in with bukkit would leave a NeoForge server switched
// to Paper still carrying TEMPLATE=neoforge - so the container would keep provisioning
// NeoForge instead of Paper.
function templateFamily(template) {
  const id = String(template || '').toLowerCase();
  if (['fabric', 'pixelmon'].includes(id)) return 'fabric';
  if (id === 'neoforge') return 'neoforge';
  return 'bukkit';
}

// Splits a version string like "1.21.5-rc1" into a numeric base [1,21,5] plus a
// suffix used to break ties (rc1, pre2, snapshot, etc).
function parseVersionParts(version) {
  const str = String(version);
  const match = str.match(/^(\d+(?:\.\d+)*)/);
  const base = match
    ? match[1].split('.').map((part) => Number.parseInt(part, 10) || 0)
    : [];
  const suffix = str.slice(match ? match[1].length : 0).toLowerCase();
  return { base, suffix };
}

function suffixRank(suffix) {
  if (!suffix) return 1000; // stable release wins
  if (suffix.startsWith('-rc')) return 900;
  if (suffix.startsWith('-pre')) return 800;
  if (suffix.startsWith('-snapshot') || suffix.includes('snapshot')) return 700;
  if (suffix.startsWith('-experimental') || suffix.includes('experimental')) return 600;
  return 500;
}

function sortSemverDesc(a, b) {
  const av = typeof a === 'string' ? parseVersionParts(a) : parseVersionParts(a?.id || '');
  const bv = typeof b === 'string' ? parseVersionParts(b) : parseVersionParts(b?.id || '');
  const maxLength = Math.max(av.base.length, bv.base.length);

  for (let i = 0; i < maxLength; i += 1) {
    const valueA = av.base[i] || 0;
    const valueB = bv.base[i] || 0;
    if (valueA !== valueB) {
      return valueB - valueA;
    }
  }

  const rankDiff = suffixRank(bv.suffix) - suffixRank(av.suffix);
  if (rankDiff !== 0) return rankDiff;

  return String(b?.id || b).localeCompare(String(a?.id || a));
}

// Accept release versions (1.21.5) AND experimental ones (1.21.5-rc1,
// 1.21-pre2, 1.21-snapshot, 26.2-rc-2, etc). Reject obviously bogus entries.
const VALID_VERSION_RE = /^\d+\.\d+(?:\.\d+)?(?:[-+][\w.+-]+)?$/;

function labelForVersion(version) {
  const lower = version.toLowerCase();
  if (lower.includes('-rc')) return `${version} (release candidate)`;
  if (lower.includes('-pre')) return `${version} (pre-release)`;
  if (lower.includes('snapshot')) return `${version} (snapshot)`;
  if (lower.includes('experimental')) return `${version} (experimental)`;
  return version;
}

async function fetchJson(url) {
  const res = await fetch(url, {
    headers: {
      'User-Agent': 'Apect Versions API/1.0',
      Accept: 'application/json',
    },
    cache: 'no-store',
  });

  if (!res.ok) {
    throw new Error(`Upstream request failed: ${res.status}`);
  }

  return res.json();
}

// ============ VERSION LISTS ============

// PaperMC's legacy v2 API (api.papermc.io/v2) has been sunset. Paper and Folia now live on
// the "Fill" downloads service (fill.papermc.io/v3), which groups versions by minor release
// (e.g. "26.2": ["26.2", "26.2-rc-2"]) - flatten that into one list, same as before.
async function getFillVersions(project) {
  const data = await fetchJson(`https://fill.papermc.io/v3/projects/${encodeURIComponent(project)}`);
  const groups = data?.versions || {};
  const all = Object.values(groups).flat().filter((v) => typeof v === 'string');
  return all
    .filter((version) => VALID_VERSION_RE.test(version))
    .sort(sortSemverDesc)
    .map((version) => ({ id: version, label: labelForVersion(version) }));
}

async function getPaperVersions() {
  return getFillVersions('paper');
}

async function getFoliaVersions() {
  return getFillVersions('folia');
}

async function getLeafVersions() {
  const data = await fetchJson('https://api.leafmc.one/v2/projects/leaf');
  const versions = Array.isArray(data?.versions) ? data.versions : [];
  return versions
    .filter((version) => VALID_VERSION_RE.test(version))
    .sort(sortSemverDesc)
    .map((version) => ({ id: version, label: labelForVersion(version) }));
}

async function getPurpurVersions() {
  const data = await fetchJson('https://api.purpurmc.org/v2/purpur');
  const versions = Array.isArray(data?.versions) ? data.versions : [];
  return versions
    .filter((version) => VALID_VERSION_RE.test(version))
    .sort(sortSemverDesc)
    .map((version) => ({ id: version, label: labelForVersion(version) }));
}

// Spigot has no official download API (SpigotMC only ships BuildTools), and Pufferfish
// only publishes per-branch Jenkins jobs, so both come from MCJars, which hosts prebuilt
// jars for every version of each.
const MCJARS_API = 'https://versions.mcjars.app/api/v2';

async function getMcjarsVersions(project) {
  const data = await fetchJson(`${MCJARS_API}/builds/${encodeURIComponent(project.toUpperCase())}`);
  const groups = data?.builds || {};
  return Object.keys(groups)
    .filter((version) => groups[version]?.type === 'RELEASE' && VALID_VERSION_RE.test(version))
    .sort(sortSemverDesc)
    .map((version) => ({ id: version, label: labelForVersion(version) }));
}

async function getVanillaVersions() {
  const data = await fetchJson('https://launchermeta.mojang.com/mc/game/version_manifest_v2.json');
  const versions = Array.isArray(data?.versions) ? data.versions : [];
  // Include releases, snapshots, pre-releases, release candidates and experimental builds.
  const allowedTypes = new Set(['release', 'snapshot', 'old_beta', 'old_alpha']);
  return versions
    .filter((version) => allowedTypes.has(version?.type))
    .slice(0, 200)
    .map((version) => {
      let label = version.id;
      if (version.type !== 'release') label = `${version.id} (${version.type})`;
      return { id: version.id, label };
    });
}

// Only the versions we can actually make work end to end - see FABRIC_VERSIONS for why
// the list is explicit rather than taken from Fabric's loader metadata.
async function getFabricVersions() {
  return FABRIC_VERSIONS.map((entry) => ({
    id: entry.id,
    label: entry.id === FABRIC_DEFAULT_VERSION ? `${entry.id} (latest)` : entry.id,
  }));
}

// Only the versions we have an ApectNeoProxy build for - see NEOFORGE_VERSIONS.
async function getNeoforgeVersions() {
  return NEOFORGE_VERSIONS.map((entry) => ({
    id: entry.id,
    label: entry.id === NEOFORGE_DEFAULT_VERSION ? `${entry.id} (latest)` : entry.id,
  }));
}

// The "build" for NeoForge is its own version number (e.g. 21.8.57), which is what the
// installer is published under.
async function getNeoforgeBuilds(version) {
  const info = neoforgeVersionInfo(version);
  if (!info) return [];
  return [{ id: info.neoforge, label: `NeoForge ${info.neoforge}`, channel: 'stable' }];
}

async function getVersionsForType(jarType) {
  if (jarType === 'paper') return getPaperVersions();
  if (jarType === 'purpur') return getPurpurVersions();
  if (jarType === 'folia') return getFoliaVersions();
  if (jarType === 'leaf') return getLeafVersions();
  if (jarType === 'pufferfish') return getMcjarsVersions('pufferfish');
  if (jarType === 'spigot') return getMcjarsVersions('spigot');
  if (jarType === 'vanilla') return getVanillaVersions();
  if (jarType === 'fabric') return getFabricVersions();
  if (jarType === 'neoforge') return getNeoforgeVersions();
  return [];
}
export {
  getNeoforgeBuilds,
  JAR_TYPES,
  SUPPORTED_JAR_TYPES,
  PROXIED_JAR_TYPES,
  allowedJarTypesForServer,
  normalizeJarType,
  templateFamily,
  parseVersionParts,
  sortSemverDesc,
  labelForVersion,
  VALID_VERSION_RE,
  MCJARS_API,
  fetchJson,
  getVersionsForType,
};
